import { Clock, Plus } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { SheetContent, SheetFooter, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { Textarea } from '@/components/ui/textarea'
import { useEmployees, useOvertimeCreate } from '@/hooks'
import { useAppSelector } from '@/store/hooks'

interface RegisterOvertimeDrawerProps {
  onClose: () => void
  lockToEmployeeId?: string
}

export function RegisterOvertimeDrawer({ onClose, lockToEmployeeId }: RegisterOvertimeDrawerProps) {
  const { t } = useTranslation()
  const storeID: string = useAppSelector((state) => state?.auth?.currentStore?.storeID) || ''
  const [employeeId, setEmployeeId] = useState<string>(lockToEmployeeId ?? '')
  const [date, setDate] = useState('')
  const [startTime, setStartTime] = useState('')
  const [endTime, setEndTime] = useState('')
  const [comment, setComment] = useState('')

  const { data: employees = [] } = useEmployees({
    params: { storeID },
    enabled: !lockToEmployeeId && !!storeID,
  })

  const { mutateAsync: createOvertime, isPending } = useOvertimeCreate()

  const submit = async () => {
    if (!employeeId || !date || !startTime || !endTime) {
      toast.error(t('leaves.register.missingFields'))
      return
    }
    try {
      await createOvertime({
        employeeID: employeeId,
        storeID,
        payload: {
          overTime: [{ overTimeDate: date, startTime, endTime }],
        },
      })
      onClose()
    } catch {
      // error toast handled in hook
    }
  }

  return (
    <SheetContent className="w-full sm:max-w-md p-0 flex flex-col">
      <SheetHeader className="px-6 py-4 border-b">
        <SheetTitle className="flex items-center gap-2">
          <Clock className="w-5 h-5 text-primary" /> {t('overtime.register.title')}
        </SheetTitle>
      </SheetHeader>
      <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
        {!lockToEmployeeId && (
          <div className="space-y-2">
            <Label variant="eyebrow">{t('leaves.register.user')}</Label>
            <Select value={employeeId} onValueChange={setEmployeeId}>
              <SelectTrigger>
                <SelectValue placeholder={t('leaves.register.selectUser')} />
              </SelectTrigger>
              <SelectContent>
                {employees.map((emp) => (
                  <SelectItem key={emp.employeeID} value={emp.employeeID}>
                    {emp.firstName} {emp.lastName}
                    {emp.shortUserName ? ` (${emp.shortUserName})` : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
        <div className="space-y-2">
          <Label variant="eyebrow">{t('overtime.register.date')}</Label>
          <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
        </div>
        <div className="space-y-2">
          <Label variant="eyebrow">{t('overtime.register.timeRange')}</Label>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">{t('leaves.register.startTime')}</Label>
              <Input type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} />
            </div>
            <div>
              <Label className="text-xs">{t('leaves.register.endTime')}</Label>
              <Input type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)} />
            </div>
          </div>
        </div>
        <div className="space-y-2">
          <Label variant="eyebrow">{t('leaves.register.comment')}</Label>
          <Textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder={t('overtime.register.commentPlaceholder')}
            rows={4}
          />
        </div>
      </div>
      <SheetFooter className="px-6 py-4 border-t">
        <Button variant="outline" onClick={onClose} disabled={isPending}>
          {t('common.cancel')}
        </Button>
        <Button onClick={() => void submit()} disabled={isPending}>
          <Plus className="w-4 h-4 mr-2" />
          {t('overtime.register.submit')}
        </Button>
      </SheetFooter>
    </SheetContent>
  )
}
